import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Megaphone, ExternalLink, Mail, TrendingUp } from "lucide-react";

/** Phase 10.3 — THIS WEEK'S COMMUNITY PULSE.
 * The exact web version of the Monday digest email, visible before subscribing. */

const ARROW_CLS = {
  "↑": "bg-amber-50 text-amber-900 border-amber-300",
  "↓": "bg-sky-50 text-sky-900 border-sky-200",
  "→": "bg-slate-50 text-slate-700 border-slate-200",
};

export default function WeeklyPulse() {
  const [params, setParams] = useSearchParams();
  const [munis, setMunis] = useState([]);
  const [muniId, setMuniId] = useState(params.get("municipality") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => {
      setMunis(r.data);
      if (!muniId && r.data.length === 1) setMuniId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!muniId) return;
    setLoading(true);
    setParams({ municipality: muniId });
    api.get("/public/digest/weekly-preview", { params: { municipality_id: muniId } })
      .then((r) => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [muniId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-3xl">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Community Voice</Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="weekly-pulse-title">
        This Week's Community Pulse
      </h1>
      <p className="text-muted-foreground mt-2">
        This page is exactly what Weekly Pulse subscribers receive by email every Monday —
        see it here first, then subscribe if you want it delivered.
      </p>

      <div className="w-full sm:w-72 mt-6">
        <Label className="text-xs text-muted-foreground">Municipality</Label>
        <Select value={muniId || undefined} onValueChange={setMuniId}>
          <SelectTrigger className="mt-1 bg-white" data-testid="weekly-muni-select"><SelectValue placeholder="Choose your municipality" /></SelectTrigger>
          <SelectContent className="bg-white">
            {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && <Skeleton className="h-80 mt-6" />}

      {data && !loading && (
        <Card className="mt-6 border-t-4 border-t-accent" data-testid="weekly-pulse-card">
          <CardContent className="p-6 sm:p-8">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {data.place} Community Pulse
            </p>
            <p className="font-heading font-semibold text-foreground" data-testid="weekly-pulse-week-of">
              Week of {new Date(data.week_of + "T12:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
            </p>

            {data.top_issue ? (
              <div className="mt-5" data-testid="weekly-pulse-top">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">#1 Community Concern</p>
                <p className="font-heading font-bold text-2xl text-foreground mt-0.5">{data.top_issue}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-5" data-testid="weekly-pulse-no-data">
                Not enough responses yet for local rankings — your voice can change that.
              </p>
            )}

            {data.trending?.length > 0 && (
              <div className="mt-4" data-testid="weekly-pulse-trending">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5 inline-flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" /> What's changing
                </p>
                <div className="space-y-1.5">
                  {data.trending.map((t) => (
                    <div key={t.label} className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className={`${ARROW_CLS[t.arrow] || ""} text-[10px]`}>{t.arrow} {t.text}</Badge>
                      <span className="text-foreground">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs font-semibold text-foreground mt-4" data-testid="weekly-pulse-count">
              {data.week_responses} community response{data.week_responses === 1 ? "" : "s"} this week
            </p>

            {data.question && (
              <div className="mt-6 rounded-lg border border-slate-200 bg-secondary p-4" data-testid="weekly-pulse-question">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                  <Megaphone className="h-3.5 w-3.5 text-accent" /> This Week's Question
                </p>
                <p className="font-heading font-semibold text-foreground">{data.question.question}</p>
                <Button asChild size="sm" className="mt-3 bg-primary hover:bg-primary/90" data-testid="weekly-pulse-answer-btn">
                  <Link to={`/polls?municipality=${muniId}`}>ANSWER THE QUESTION</Link>
                </Button>
              </div>
            )}

            {data.pita && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="weekly-pulse-pita">
                <p className="font-heading font-semibold text-sm text-amber-900">Actual municipal problem?</p>
                <p className="text-xs text-amber-900/90 mt-0.5">Report it. Map it. Track it. Get answers.</p>
                <Button asChild size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white" data-testid="weekly-pulse-pita-btn">
                  <a href={data.pita.url} target="_blank" rel="noopener noreferrer">
                    Report it in PITA <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                  </a>
                </Button>
                <p className="text-[10px] text-amber-800/80 mt-2">{data.pita.note}</p>
              </div>
            )}

            <p className="text-[10px] text-muted-foreground italic mt-5" data-testid="weekly-pulse-disclaimer">{data.disclaimer}</p>
          </CardContent>
        </Card>
      )}

      <Card className="mt-5" data-testid="weekly-pulse-subscribe-cta">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground inline-flex items-center gap-2">
            <Mail className="h-4 w-4 text-accent" /> Want this delivered every Monday morning?
          </p>
          <Button asChild size="sm" variant="outline" data-testid="weekly-pulse-subscribe-link">
            <Link to={`/community-voice${muniId ? `?municipality=${muniId}` : ""}#digest`}>Subscribe to the Weekly Pulse</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
